import { BaseEntityState, RootState } from "@store/store-helper";
import { AdmPlan } from "@custom-types/base-types";
import { plansAdapter } from "@store/plans/plans-slice";
import { createSelector } from "@reduxjs/toolkit";
import { SCENE_SEAT_LABEL } from "@components/plans-table/scene-licenses";
import {
  calculateSceneUsedSeats,
  isAttachedTokenPlan,
  isSceneSeatPlan,
} from "@utils/plan-utils";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { isActiveUsersLimitType } from "@utils/limit-utils";

/**
 * List of feature identifiers that should be hidden in the admin panel
 * Plans that include any of these features will not be shown in the admin panel,
 * unless the `shouldShowAllPlans` dev feature is enabled.
 */
const invisibleBundles = new Set<string>([
  // Plans with the minimal feature are not relevant to handle in the admin panel
  // This is because those plans are assigned to every company
  "minimal",
]);

interface AllPlansSelectorParams {
  /** If true, the selector will return all plans including invisible bundles without considering the dev feature flag */
  shouldShowAllPlans?: boolean;
}

/** Selector that returns plans based on the subject type */
export function allPlansSelector({
  shouldShowAllPlans,
}: AllPlansSelectorParams = {}): (state: RootState) => AdmPlan[] {
  return createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      const plans: AdmPlan[] = plansAdapter
        .getSelectors()
        .selectAll(state.plans);

      if (shouldShowAllPlans || state.devFeatures.shouldShowAllPlans) {
        return plans;
      }

      return plans.filter(
        (plan) =>
          // The tokens of a plan are reported by the plan itself, so the token plans that grant
          // them would only repeat what the plan already shows
          !isAttachedTokenPlan(plan) &&
          !plan.featureBundles.some(({ identifier }) =>
            invisibleBundles.has(identifier)
          )
      );
    }
  );
}

/** Selector that returns a map of plans */
export function allPlansMapSelector(): (
  state: RootState
) => BaseEntityState<AdmPlan>["entities"] {
  return createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      return state.plans.entities;
    }
  );
}

/** Return the selected plan by providing the ID */
export function getPlanByIdSelector(
  planId: AdmPlan["id"]
): (state: RootState) => AdmPlan | undefined {
  return createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      const plan: AdmPlan = plansAdapter
        .getSelectors()
        .selectById(state.plans, planId);

      return plan;
    }
  );
}

/**
 * Returns the active members limit for a given plan ID.
 */
export function activeMembersLimitSelector(
  planId: AdmPlan["id"]
): (state: RootState) => FaroCoreApiTypes.LimitData | undefined {
  return createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      const plan: AdmPlan = plansAdapter
        .getSelectors()
        .selectById(state.plans, planId);

      if (!plan) {
        return undefined;
      }

      const activeMemberLimit = plan.constraints.find((constraint) =>
        isActiveUsersLimitType(constraint.type)
      );

      return activeMemberLimit?.data;
    }
  );
}

/**
 * Selector that returns the used and total scene seats for a given plan ID.
 * @param planId The plan to see attached scene seats for.
 * @returns The used and total scene seats for the given plan ID, or undefined if the plan does not exist
 *          or has no attached scene seats.
 */
export function sceneSeatsLimitSelector(
  planId: AdmPlan["id"]
): (state: RootState) => undefined | { usedSeats: number; totalSeats: number } {
  return createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      const plan: AdmPlan = plansAdapter
        .getSelectors()
        .selectById(state.plans, planId);

      // Skip the SCENE Seats plans as they cannot have any attached plans to them
      if (!plan || plan.label === SCENE_SEAT_LABEL) {
        return undefined;
      }

      const allPlans = plansAdapter.getSelectors().selectAll(state.plans);

      const attachedToThisPlan = allPlans.filter(
        // Find all SCENE seats that have the same parent subscription ID as the provided plan ID.
        // Token plans are attached the same way, so they have to be left out here.
        (plan) =>
          plan.metadata?.parentSubscriptionId === planId &&
          isSceneSeatPlan(plan)
      );

      if (!attachedToThisPlan.length) {
        return undefined;
      }

      return {
        usedSeats: calculateSceneUsedSeats(attachedToThisPlan),
        totalSeats: attachedToThisPlan.length,
      };
    }
  );
}

/**
 * Selector that returns the token plans attached to a given plan, in the order they run.
 *
 * Every year of a plan is granted by its own token plan, so a plan that runs for several years
 * has several of them.
 */
export function attachedTokenPlansSelector(
  planId: AdmPlan["id"]
): (state: RootState) => AdmPlan[] {
  return createSelector(
    (state: RootState) => state.plans,
    (plansState) => {
      const allPlans = plansAdapter.getSelectors().selectAll(plansState);

      return allPlans
        .filter(
          (plan) =>
            plan.metadata?.parentSubscriptionId === planId &&
            isAttachedTokenPlan(plan)
        )
        .sort((a, b) => Date.parse(a.startDate) - Date.parse(b.startDate));
    }
  );
}

/**
 * Selector that returns the tokens a plan currently grants, i.e. the ones of the token plans that
 * are active right now. Years that have passed or have not started yet are left out, so that the
 * number reflects what the workspace can spend today.
 *
 * @returns the consumed and the total tokens, or undefined when no token plan is active
 */
export function activeTokensSelector(
  planId: AdmPlan["id"]
): (state: RootState) => undefined | { consumed: number; total: number } {
  return createSelector(attachedTokenPlansSelector(planId), (tokenPlans) => {
    const activeTokenPlans = tokenPlans.filter(
      (plan) => plan.status.identifier === "active"
    );

    if (!activeTokenPlans.length) {
      return undefined;
    }

    return activeTokenPlans.reduce(
      (acc, plan) => {
        for (const constraint of plan.constraints) {
          if (constraint.type === "CREDITS") {
            acc.consumed += constraint.data.current ?? 0;
            acc.total += constraint.data.max;
          }
        }
        return acc;
      },
      { consumed: 0, total: 0 }
    );
  });
}
